import { 
  getListasLocal, getListaByIdLocal, saveListaLocal, deleteListaLocal, closeListaLocal,
  saveListaItemLocal, deleteListaItemLocal, toggleListaItemLocal, saveListaMensagemLocal,
  searchHistoricalProducts, getNotasLocal, getListsWithQuotesMap
} from '../db.js';
import { escapeHTML } from '../security.js';
import { authManager, getSavedScope, setSavedScope } from '../auth-manager.js';
import { getCategoryIcon, DEFAULT_CATEGORIES } from '../categorizer.js';

export async function renderListasView(container, param, syncManager) {
  let currentScope = getSavedScope() || 'personal';
  let activeFilter = 'all'; // all, semanal, mensal, fechada
  let selectedListId = param ? Number(param) : null;
  let allListas = [];
  let currentList = null;
  let listsWithQuotes = new Set();

  // 1. Background cloud sync
  async function syncListsFromCloud() {
    if (!authManager.isAuthenticated()) return;
    try {
      const res = await authManager.fetchWithAuth(`/api/listas?scope=${currentScope}`);
      if (res.ok) {
        const apiLists = await res.json();
        allListas = apiLists;
        if (window.location.hash.startsWith('#listas') || window.location.hash === '#dashboard') {
          render();
        }
        // Save cloud lists to local IndexedDB asynchronously
        Promise.all((apiLists || []).map(l => saveListaLocal(l))).catch(() => {});
      }
    } catch (e) {
      console.warn('[renderListasView] Cloud sync error:', e);
      if (window.location.hash.startsWith('#listas') || window.location.hash === '#dashboard') {
        render();
      }
    }
  }

  // 2. Cache-first load function
  async function fetchListas() {
    allListas = await getListasLocal(currentScope).catch(() => []);
    try {
      listsWithQuotes = await getListsWithQuotesMap();
    } catch (e) {}

    if (allListas && allListas.length > 0) {
      if (window.location.hash.startsWith('#listas') || window.location.hash === '#dashboard') {
        render();
      }
      syncListsFromCloud();
    } else {
      await syncListsFromCloud();
    }
  }

  await fetchListas();

  function render() {
    const isPersonal = currentScope === 'personal';
    const isFamily = currentScope === 'family';

    // Summary calculations
    const isClosed = l => l.status === 'fechada' || l.status === 'closed' || l.status === 'concluida';
    const isSemanal = l => (l.type === 'semanal' || l.tipo === 'semanal');
    const isMensal = l => (l.type === 'mensal' || l.tipo === 'mensal');
    const isAvulsa = l => (l.type === 'avulsa' || l.tipo === 'avulsa' || (!isSemanal(l) && !isMensal(l)));

    const activeLists = allListas.filter(l => !isClosed(l));
    const closedLists = allListas.filter(l => isClosed(l));
    const semanalActive = activeLists.filter(isSemanal);
    const mensalActive = activeLists.filter(isMensal);
    const avulsaActive = activeLists.filter(isAvulsa);

    const totalEstimated = activeLists.reduce((sum, l) => sum + (l.totalEstimated || l.totalEstimado || 0), 0);
    const pendingItemsCount = activeLists.reduce((sum, l) => {
      const items = l.itemCount || 0;
      const checked = l.checkedCount || 0;
      return sum + Math.max(0, items - checked);
    }, 0);

    // Filter lists - all filters show active lists except 'fechada'
    const filteredListas = (() => {
      if (activeFilter === 'semanal') return semanalActive;
      if (activeFilter === 'mensal') return mensalActive;
      if (activeFilter === 'avulsa') return avulsaActive;
      if (activeFilter === 'fechada') return closedLists;
      return activeLists; // 'all' shows all active lists
    })();

    container.innerHTML = `
      <!-- Header com Switcher de Escopo -->
      <div class="section-header" style="display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; margin-bottom: 1.25rem;">
        <div>
          <h2 class="section-title" style="margin: 0; font-size: clamp(1.25rem, 4.5vw, 1.6rem); font-weight: 800; color: #111827; line-height: 1.2;">Listas de Compras</h2>
          <p class="section-subtitle" style="margin: 0.15rem 0 0 0; font-size: 0.82rem; color: #64748B;">Planejamento inteligente e colaborativo</p>
        </div>
        <div class="scope-selector" id="listas-scope-selector">
          <button class="scope-pill ${isPersonal ? 'active' : ''}" data-scope="personal">Pessoal</button>
          <button class="scope-pill ${isFamily ? 'active' : ''}" data-scope="family">Família</button>
        </div>
      </div>

      <!-- Action & Filter Pills Row -->
      <div style="display: flex; align-items: center; justify-content: space-between; gap: 0.5rem; margin-bottom: 1.25rem; overflow-x: auto; scrollbar-width: none; -webkit-overflow-scrolling: touch; padding-bottom: 2px;">
        <button id="btn-create-list" class="btn btn-primary" style="display: inline-flex; align-items: center; justify-content: center; gap: 0.35rem; height: 36px; min-height: 36px; padding: 0 0.95rem; border-radius: var(--radius-pill); font-weight: 700; font-size: 0.82rem; flex-shrink: 0; white-space: nowrap; line-height: 1;">
          <span class="material-symbols-outlined" style="font-size: 1.1rem; line-height: 1;">add</span>
          Nova Lista
        </button>

        <div class="home-scope-switcher" style="display: inline-flex; align-items: center; gap: 3px; flex-shrink: 0;">
          <button class="home-scope-pill ${activeFilter === 'all' ? 'active' : ''}" data-filter="all" style="height: 32px; font-size: 0.8rem; padding: 0 0.8rem; display: inline-flex; align-items: center;">Todas (${activeLists.length})</button>
          <button class="home-scope-pill ${activeFilter === 'semanal' ? 'active' : ''}" data-filter="semanal" style="height: 32px; font-size: 0.8rem; padding: 0 0.8rem; display: inline-flex; align-items: center;">Semanal (${semanalActive.length})</button>
          <button class="home-scope-pill ${activeFilter === 'mensal' ? 'active' : ''}" data-filter="mensal" style="height: 32px; font-size: 0.8rem; padding: 0 0.8rem; display: inline-flex; align-items: center;">Mensal (${mensalActive.length})</button>
          <button class="home-scope-pill ${activeFilter === 'avulsa' ? 'active' : ''}" data-filter="avulsa" style="height: 32px; font-size: 0.8rem; padding: 0 0.8rem; display: inline-flex; align-items: center;">Avulsa (${avulsaActive.length})</button>
          <button class="home-scope-pill ${activeFilter === 'fechada' ? 'active' : ''}" data-filter="fechada" style="height: 32px; font-size: 0.8rem; padding: 0 0.8rem; display: inline-flex; align-items: center;">Fechadas (${closedLists.length})</button>
        </div>
      </div>

      <!-- Summary Carousel Cards (Identical to Home page) -->
      <div class="home-carousel-wrapper" style="margin-bottom: 1.25rem;">
        <div class="home-carousel-container">
          
          <!-- 1. Listas Ativas -->
          <div class="home-kpi-card">
            <div class="home-card-header">
              <span class="home-card-label">Listas Ativas</span>
              <span class="material-symbols-outlined" style="font-size: 18px; color: var(--color-primary-text, #2563EB);">checklist</span>
            </div>
            <div class="home-card-value font-mono" style="font-size: 1.6rem; font-weight: 800; color: var(--text-main);">
              ${activeLists.length}
            </div>
            <div class="home-card-footer">
              <span class="home-card-sublabel">Em andamento (${allListas.length} total)</span>
            </div>
          </div>

          <!-- 2. Total Estimado / Orçamento -->
          <div class="home-kpi-card">
            <div class="home-card-header">
              <span class="home-card-label">Total Estimado</span>
              <span class="material-symbols-outlined" style="font-size: 18px; color: #D97706;">payments</span>
            </div>
            <div class="home-card-value font-mono money-val" style="font-size: 1.45rem; font-weight: 800; color: #D97706;">
              R$ ${totalEstimated.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div class="home-card-footer">
              <span class="home-card-sublabel">Orçamento previsto</span>
            </div>
          </div>

          <!-- 3. Itens a Comprar -->
          <div class="home-kpi-card">
            <div class="home-card-header">
              <span class="home-card-label">Itens Pendentes</span>
              <span class="material-symbols-outlined" style="font-size: 18px; color: #10B981;">shopping_cart</span>
            </div>
            <div class="home-card-value font-mono" style="font-size: 1.6rem; font-weight: 800; color: #10B981;">
              ${pendingItemsCount}
            </div>
            <div class="home-card-footer">
              <span class="home-card-sublabel">A comprar no mercado</span>
            </div>
          </div>

        </div>
      </div>

      <!-- Lists Cards Stream -->
      <div id="listas-stream-container" style="display: flex; flex-direction: column; gap: 1rem; margin-bottom: 2rem;">
        ${filteredListas.length === 0 ? `
          <div class="chart-card" style="text-align: center; padding: 2.5rem 1.5rem;">
            <span class="material-symbols-outlined" style="font-size: 48px; color: var(--color-on-surface-variant); opacity: 0.4; margin-bottom: 0.5rem;">checklist_rtl</span>
            <h3 style="font-size: 1.15rem; font-weight: 800; margin: 0 0 0.4rem 0; color: #111827;">Nenhuma lista encontrada</h3>
            <p class="text-muted" style="font-size: 0.875rem; margin: 0 0 1.25rem 0;">Crie sua primeira lista de compras semanal ou mensal para organizar os gastos com sua família.</p>
            <button id="btn-create-list-empty" class="btn btn-primary" style="display: inline-flex; align-items: center; gap: 0.4rem; padding: 0.65rem 1.25rem; border-radius: var(--radius-pill); font-weight: 700;">
              <span class="material-symbols-outlined">add</span>
              Criar Lista de Compras
            </button>
          </div>
        ` : filteredListas.map(l => {
          const closed = isClosed(l);
          const typeLabel = isMensal(l) ? 'Mensal' : (isSemanal(l) ? 'Semanal' : 'Avulsa');
          const typeBadgeColor = isMensal(l) ? '#3B82F6' : (isSemanal(l) ? '#10B981' : '#8B5CF6');
          const itemCount = l.itemCount || 0;
          const checkedCount = l.checkedCount || 0;
          const progressPct = itemCount > 0 ? Math.round((checkedCount / itemCount) * 100) : 0;
          const estVal = l.totalEstimated || l.totalEstimado || 0;
          const actVal = l.totalActual || l.totalReal || 0;
          const msgCount = l.messageCount || 0;

          return `
            <div class="chart-card lista-card-item" data-id="${l.id}" style="padding: 1.35rem; transition: transform 0.15s ease, box-shadow 0.15s ease; cursor: pointer;">
              <div style="display: flex; align-items: flex-start; justify-content: space-between; gap: 0.5rem; margin-bottom: 0.75rem;">
                <div style="flex: 1; min-width: 0;">
                  <div style="display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; margin-bottom: 0.35rem;">
                    <h3 style="margin: 0; font-size: 1.15rem; font-weight: 800; color: #111827; word-break: break-word;">${escapeHTML(l.name || l.nome || 'Lista de Compras')}</h3>
                    <span style="font-size: 0.72rem; font-weight: 800; padding: 0.2rem 0.6rem; border-radius: var(--radius-pill); background: ${typeBadgeColor}15; color: ${typeBadgeColor}; text-transform: uppercase;">${typeLabel}</span>
                    ${closed ? `<span style="font-size: 0.72rem; font-weight: 800; padding: 0.2rem 0.6rem; border-radius: var(--radius-pill); background: #6B728020; color: #6B7280; text-transform: uppercase;">Fechada ✓</span>` : ''}
                    ${(l.linkedNotas && l.linkedNotas.length > 0) ? `
                      <span style="font-size: 0.72rem; font-weight: 800; padding: 0.2rem 0.6rem; border-radius: var(--radius-pill); background: rgba(16, 185, 129, 0.15); color: #10B981; text-transform: uppercase; display: inline-flex; align-items: center; gap: 0.25rem;">
                        <span class="material-symbols-outlined" style="font-size: 13px;">receipt_long</span> ${l.linkedNotas.length} ${l.linkedNotas.length === 1 ? 'NFC-e' : 'NFC-es'}
                      </span>
                    ` : ''}
                  </div>
                  <div class="text-muted" style="font-size: 0.82rem; font-weight: 500;">
                    Ref: ${escapeHTML(l.monthRef || l.mesRef || 'Geral')} &bull; Criada em ${new Date(l.createdAt || Date.now()).toLocaleDateString('pt-BR')}
                  </div>
                </div>

                <div style="display: flex; align-items: center; gap: 0.25rem;">
                  <button class="btn-icon btn-edit-lista" data-id="${l.id}" title="Editar Nome da Lista" aria-label="Editar nome da lista" style="background: none; border: none; color: #94A3B8; cursor: pointer; padding: 0.35rem; border-radius: 0.5rem;">
                    <span class="material-symbols-outlined" style="font-size: 1.25rem;">edit</span>
                  </button>
                  ${itemCount === 0 && !closed ? `
                    <button class="btn-icon btn-delete-lista" data-id="${l.id}" title="Excluir Lista Vazia" style="background: none; border: none; color: #94A3B8; cursor: pointer; padding: 0.35rem; border-radius: 0.5rem;">
                      <span class="material-symbols-outlined" style="font-size: 1.25rem;">delete</span>
                    </button>
                  ` : ''}
                </div>
              </div>

              <!-- Progress Bar -->
              <div style="margin-bottom: 0.95rem;">
                <div style="display: flex; justify-content: space-between; align-items: center; font-size: 0.82rem; margin-bottom: 0.4rem;">
                  <span style="font-weight: 700; color: #374151;">Progresso do Carrinho</span>
                  <span class="font-mono" style="font-weight: 800; color: ${progressPct === 100 ? '#10B981' : '#D97706'};">${checkedCount}/${itemCount} itens (${progressPct}%)</span>
                </div>
                <div style="height: 8px; background: #ECEAE3; border-radius: var(--radius-pill); overflow: hidden;">
                  <div style="height: 100%; width: ${progressPct}%; background: ${progressPct === 100 ? '#10B981' : '#D97706'}; border-radius: var(--radius-pill); transition: width 0.3s ease;"></div>
                </div>
              </div>

              <!-- Values & Chat stats Row -->
              <div style="display: flex; align-items: center; justify-content: space-between; padding-top: 0.85rem; border-top: 1px solid var(--border-color); font-size: 0.85rem; margin-bottom: 0.75rem;">
                <div style="display: flex; align-items: center; gap: 1.25rem;">
                  <div>
                    <span class="text-muted" style="font-size: 0.74rem; font-weight: 600; display: block;">Estimado</span>
                    <span class="font-mono font-bold" style="font-size: 1.05rem; font-weight: 800; color: #111827;">R$ ${estVal.toFixed(2).replace('.', ',')}</span>
                  </div>
                  <div>
                    <span class="text-muted" style="font-size: 0.74rem; font-weight: 600; display: block;">Comprado</span>
                    <span class="font-mono font-bold" style="font-size: 1.05rem; font-weight: 800; color: #10B981;">R$ ${actVal.toFixed(2).replace('.', ',')}</span>
                  </div>
                </div>

                ${msgCount > 0 ? `
                  <div style="display: flex; align-items: center; gap: 0.3rem; color: #3B82F6; font-weight: 700; font-size: 0.8rem; background: #EFF6FF; padding: 0.3rem 0.65rem; border-radius: var(--radius-pill);">
                    <span class="material-symbols-outlined" style="font-size: 14px;">chat</span>
                    <span>${msgCount}</span>
                  </div>
                ` : ''}
              </div>

              <!-- Action Buttons Row (Unified Standard Style) -->
              <div style="display: flex; align-items: center; gap: 0.45rem; flex-wrap: wrap;">
                <button class="btn btn-secondary btn-cotar-lista" data-id="${l.id}" title="Cotar preços nos supermercados" style="padding: 0.42rem 0.75rem; font-size: 0.78rem; border-radius: var(--radius-pill); font-weight: 700; display: inline-flex; align-items: center; justify-content: center; gap: 0.35rem; flex: 1; min-width: 85px;">
                  <span class="material-symbols-outlined" style="font-size: 16px; color: var(--text-muted);">photo_camera</span>
                  <span>Cotar</span>
                </button>

                ${listsWithQuotes.has(Number(l.id)) ? `
                  <button class="btn btn-secondary btn-comparativo-lista" data-id="${l.id}" title="Ver comparativo e ranking de supermercados" style="padding: 0.42rem 0.75rem; font-size: 0.78rem; border-radius: var(--radius-pill); font-weight: 700; display: inline-flex; align-items: center; justify-content: center; gap: 0.35rem; flex: 1; min-width: 105px;">
                    <span class="material-symbols-outlined" style="font-size: 16px; color: var(--text-muted);">analytics</span>
                    <span>Comparativo</span>
                  </button>
                ` : ''}

                <button class="btn btn-secondary btn-open-lista" data-id="${l.id}" style="padding: 0.42rem 0.85rem; font-size: 0.78rem; border-radius: var(--radius-pill); font-weight: 700; display: inline-flex; align-items: center; justify-content: center; gap: 0.35rem; flex: 1.3; min-width: 130px;">
                  <span>Ver Itens &amp; Chat</span>
                  <span class="material-symbols-outlined" style="font-size: 15px; color: var(--text-muted);">arrow_forward</span>
                </button>
              </div>
            </div>
          `;
        }).join('')}
      </div>

      <!-- Detail Slide-Over / Modal Container (if list selected) -->
      <div id="lista-detail-modal-container"></div>

      <!-- Create List Modal Container -->
      <div id="create-lista-modal-container"></div>

      <!-- Delete Confirmation Modal -->
      <div id="delete-lista-overview-modal" class="modal-overlay hidden" style="position: fixed; inset: 0; background: rgba(0, 0, 0, 0.65); backdrop-filter: blur(8px); z-index: 9999; display: flex; align-items: center; justify-content: center; padding: 1rem;">
        <div class="modal-card" style="background: var(--bg-surface); border-radius: 1.25rem; border: 1px solid var(--border-color); box-shadow: var(--shadow-card); max-width: 400px; width: 100%; padding: 1.75rem 1.5rem; text-align: center;">
          <div style="width: 52px; height: 52px; border-radius: 50%; background: var(--color-danger-bg); color: var(--color-danger); display: flex; align-items: center; justify-content: center; margin: 0 auto 1rem auto; border: 1px solid var(--color-danger-border);">
            <span class="material-symbols-outlined" style="font-size: 28px;">delete_forever</span>
          </div>
          <h3 style="font-weight: 800; font-size: 1.15rem; color: var(--text-main); margin: 0 0 0.4rem 0;">Excluir Lista de Compras?</h3>
          <p class="text-muted" style="font-size: 0.85rem; line-height: 1.45; margin: 0 0 1.25rem 0;">
            Esta ação removerá permanentemente a lista, todos os seus produtos e o histórico de recados do sistema.
          </p>
          <div style="display: flex; gap: 0.5rem;">
            <button class="btn btn-secondary" id="btn-cancel-delete-overview" style="flex: 1; border-radius: var(--radius-pill); font-weight: 700;">
              Cancelar
            </button>
            <button class="btn btn-danger" id="btn-confirm-delete-overview" style="flex: 1; border-radius: var(--radius-pill); font-weight: 700; background: #DC2626; color: #FFF;">
              Sim, Excluir
            </button>
          </div>
        </div>
      </div>
    `;

    bindEvents();
  }

  function bindEvents() {
    // Scope Switcher
    const scopeSwitcher = container.querySelector('#listas-scope-selector');
    if (scopeSwitcher) {
      scopeSwitcher.querySelectorAll('.scope-pill').forEach(btn => {
        btn.addEventListener('click', async () => {
          const targetScope = btn.getAttribute('data-scope');
          if (targetScope !== currentScope) {
            currentScope = targetScope;
            setSavedScope(targetScope);
            await fetchListas();
            render();
          }
        });
      });
    }

    // Global Scope Synchronization listener
    window.addEventListener('nfce-scope-changed', async (e) => {
      const targetScope = e.detail?.scope;
      if (targetScope && targetScope !== currentScope) {
        currentScope = targetScope;
        await fetchListas();
        render();
      }
    });

    // Filter pills
    container.querySelectorAll('[data-filter]').forEach(btn => {
      btn.addEventListener('click', () => {
        activeFilter = btn.getAttribute('data-filter');
        render();
      });
    });

    // Create List Buttons
    const btnCreate = container.querySelector('#btn-create-list');
    const btnCreateEmpty = container.querySelector('#btn-create-list-empty');
    if (btnCreate) btnCreate.addEventListener('click', openCreateListModal);
    if (btnCreateEmpty) btnCreateEmpty.addEventListener('click', openCreateListModal);

    // Cotar Preços -> Navigates to dedicated page #lista-cotacao/:id
    container.querySelectorAll('.btn-cotar-lista').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = Number(btn.getAttribute('data-id'));
        if (id) {
          window.location.hash = `#lista-cotacao/${id}`;
        }
      });
    });

    // Comparativo -> Navigates to dedicated page #lista-comparativo/:id
    container.querySelectorAll('.btn-comparativo-lista').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = Number(btn.getAttribute('data-id'));
        if (id) {
          sessionStorage.setItem('comparativo_origin_hash', '#listas');
          window.location.hash = `#lista-comparativo/${id}`;
        }
      });
    });

    // Open List Detail -> Navigates to dedicated page #lista/:id
    container.querySelectorAll('.btn-open-lista, .lista-card-item').forEach(el => {
      el.addEventListener('click', (e) => {
        if (e.target.closest('.btn-delete-lista') || e.target.closest('.btn-edit-lista') || e.target.closest('.btn-cotar-lista') || e.target.closest('.btn-comparativo-lista')) return;
        const id = Number(el.getAttribute('data-id') || el.closest('.lista-card-item')?.getAttribute('data-id'));
        if (id) {
          window.location.hash = `#lista/${id}`;
        }
      });
    });

    // Delete List with in-app modal
    let pendingDeleteId = null;
    const deleteModal = container.querySelector('#delete-lista-overview-modal');
    const btnCancel = container.querySelector('#btn-cancel-delete-overview');
    const btnConfirm = container.querySelector('#btn-confirm-delete-overview');

    if (deleteModal) {
      btnCancel?.addEventListener('click', () => {
        deleteModal.classList.add('hidden');
        pendingDeleteId = null;
      });

      deleteModal.addEventListener('click', (e) => {
        if (e.target === deleteModal) {
          deleteModal.classList.add('hidden');
          pendingDeleteId = null;
        }
      });

      btnConfirm?.addEventListener('click', async () => {
        if (!pendingDeleteId) return;
        const id = pendingDeleteId;
        deleteModal.classList.add('hidden');
        pendingDeleteId = null;

        try {
          if (authManager.isAuthenticated()) {
            await authManager.fetchWithAuth(`/api/listas/${id}`, { method: 'DELETE' }).catch(() => {});
          }
          await deleteListaLocal(id);
          if (syncManager?.showToast) {
            syncManager.showToast('Lista excluída com sucesso.', 'info');
          }
          await fetchListas();
          render();
        } catch (err) {
          console.error('Erro ao excluir lista:', err);
          if (syncManager?.showToast) {
            syncManager.showToast('Erro ao excluir lista.', 'warning');
          }
        }
      });
    }

    container.querySelectorAll('.btn-delete-lista').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const id = Number(btn.getAttribute('data-id'));
        pendingDeleteId = id;
        if (deleteModal) {
          deleteModal.classList.remove('hidden');
        }
      });
    });

    // Rename List
    container.querySelectorAll('.btn-edit-lista').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        openRenameListModal(Number(btn.getAttribute('data-id')));
      });
    });
  }

  function openRenameListModal(id) {
    const modalContainer = container.querySelector('#create-lista-modal-container');
    const lista = allListas.find(l => Number(l.id) === id);
    if (!modalContainer || !lista) return;

    const currentName = lista.name || lista.nome || '';

    modalContainer.innerHTML = `
      <div class="modal-backdrop" id="modal-rename-backdrop" style="position: fixed; inset: 0; background: rgba(0,0,0,0.65); backdrop-filter: blur(4px); z-index: 1100; display: flex; align-items: center; justify-content: center; padding: 1.25rem; box-sizing: border-box;">
        <div class="modal-card chart-card" style="width: 100%; max-width: 440px; padding: 1.5rem; max-height: 90vh; overflow-y: auto; box-sizing: border-box; background: var(--bg-surface); border: 1px solid var(--border-color); border-radius: 1.5rem; box-shadow: var(--shadow-card);">
          <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 1.25rem;">
            <h3 style="margin: 0; font-size: 1.2rem; font-weight: 800; color: var(--text-main);">Editar Nome da Lista</h3>
            <button type="button" id="btn-close-rename-modal" aria-label="Fechar" style="background: none; border: none; color: var(--text-muted); cursor: pointer; padding: 0.35rem; display: flex; align-items: center;">
              <span class="material-symbols-outlined">close</span>
            </button>
          </div>

          <form id="form-rename-lista" novalidate style="display: flex; flex-direction: column; gap: 1.15rem; width: 100%;">
            <div style="display: flex; flex-direction: column; width: 100%;">
              <label for="input-rename-list" style="display: block; font-size: 0.8rem; font-weight: 700; color: var(--text-main); margin-bottom: 0.35rem;">Nome da Lista *</label>
              <input type="text" id="input-rename-list" value="${escapeHTML(currentName)}" style="width: 100%; box-sizing: border-box; padding: 0.75rem 0.85rem; border: 1px solid var(--border-color); border-radius: 0.65rem; font-size: 0.95rem; background: var(--bg-surface-alt); color: var(--text-main); outline: none; font-family: var(--font-body); transition: border-color 0.2s, box-shadow 0.2s;" />
              <span id="modal-rename-error" style="display: none; color: #EF4444; font-size: 0.75rem; font-weight: 600; margin-top: 0.35rem;"></span>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 0.75rem; margin-top: 0.5rem;">
              <button type="button" id="btn-cancel-rename-modal" class="btn btn-secondary" style="padding: 0.65rem 1.15rem; border-radius: var(--radius-pill); font-weight: 700; cursor: pointer;">Cancelar</button>
              <button type="submit" id="btn-save-rename" class="btn btn-primary" style="padding: 0.65rem 1.35rem; border-radius: var(--radius-pill); font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 0.4rem; min-width: 120px;">Salvar</button>
            </div>
          </form>
        </div>
      </div>
    `;

    const backdrop = modalContainer.querySelector('#modal-rename-backdrop');
    const form = modalContainer.querySelector('#form-rename-lista');
    const btnSave = modalContainer.querySelector('#btn-save-rename');
    const inputName = modalContainer.querySelector('#input-rename-list');
    const errorMsg = modalContainer.querySelector('#modal-rename-error');
    const close = () => { modalContainer.innerHTML = ''; };

    modalContainer.querySelector('#btn-close-rename-modal')?.addEventListener('click', close);
    modalContainer.querySelector('#btn-cancel-rename-modal')?.addEventListener('click', close);
    backdrop?.addEventListener('click', (e) => {
      if (e.target === backdrop) close();
    });

    const showError = (msg) => {
      inputName.style.borderColor = '#EF4444';
      inputName.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.2)';
      errorMsg.textContent = msg;
      errorMsg.style.display = 'block';
    };

    inputName.addEventListener('input', () => {
      inputName.style.borderColor = 'var(--border-color)';
      inputName.style.boxShadow = 'none';
      errorMsg.style.display = 'none';
    });
    inputName.focus();
    inputName.select();

    let isSubmitting = false;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (isSubmitting) return;

      const name = inputName.value.trim();
      if (!name) {
        showError('Por favor, digite o nome da lista.');
        inputName.focus();
        return;
      }
      if (name === currentName) {
        close();
        return;
      }

      isSubmitting = true;
      btnSave.disabled = true;
      btnSave.style.opacity = '0.7';
      btnSave.innerHTML = `<span class="material-symbols-outlined spinning" style="font-size: 16px;">progress_activity</span> Salvando...`;

      try {
        const { items, messages, ...listaData } = lista;
        const updated = { ...listaData, name, nome: name };

        if (authManager.isAuthenticated()) {
          // PUT overwrites every column, so send the full list record
          const res = await authManager.fetchWithAuth(`/api/listas/${id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updated),
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
        }

        const localExisting = await getListaByIdLocal(id).catch(() => null);
        if (localExisting) {
          const { items: _i, messages: _m, ...localData } = localExisting;
          await saveListaLocal({ ...localData, name, nome: name });
        } else {
          await saveListaLocal(updated);
        }

        lista.name = name;
        lista.nome = name;
        close();
        syncManager?.showToast?.('Nome da lista atualizado.', 'info');
        render();
      } catch (err) {
        console.error('Erro ao renomear lista:', err);
        isSubmitting = false;
        btnSave.disabled = false;
        btnSave.style.opacity = '1';
        btnSave.innerHTML = 'Salvar';
        showError('Erro ao salvar o nome. Tente novamente.');
      }
    });
  }

  function openCreateListModal() {
    const modalContainer = container.querySelector('#create-lista-modal-container');
    if (!modalContainer) return;

    const currentMonthStr = new Date().toISOString().slice(0, 7);

    modalContainer.innerHTML = `
      <div class="modal-backdrop" id="modal-create-backdrop" style="position: fixed; inset: 0; background: rgba(0,0,0,0.65); backdrop-filter: blur(4px); z-index: 1100; display: flex; align-items: center; justify-content: center; padding: 1.25rem; box-sizing: border-box;">
        <div class="modal-card chart-card" style="width: 100%; max-width: 440px; padding: 1.5rem; max-height: 90vh; overflow-y: auto; box-sizing: border-box; background: var(--bg-surface); border: 1px solid var(--border-color); border-radius: 1.5rem; box-shadow: var(--shadow-card);">
          <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 1.25rem;">
            <h3 style="margin: 0; font-size: 1.2rem; font-weight: 800; color: var(--text-main);">Criar Nova Lista</h3>
            <button id="btn-close-create-modal" aria-label="Fechar" style="background: none; border: none; color: var(--text-muted); cursor: pointer; padding: 0.35rem; display: flex; align-items: center;">
              <span class="material-symbols-outlined">close</span>
            </button>
          </div>

          <form id="form-create-lista" novalidate style="display: flex; flex-direction: column; gap: 1.15rem; width: 100%;">
            <div style="display: flex; flex-direction: column; width: 100%;">
              <label style="display: block; font-size: 0.8rem; font-weight: 700; color: var(--text-main); margin-bottom: 0.35rem;">Nome da Lista *</label>
              <input type="text" id="input-list-name" placeholder="Ex: Feira da Semana, Abastecimento Mensal" style="width: 100%; box-sizing: border-box; padding: 0.75rem 0.85rem; border: 1px solid var(--border-color); border-radius: 0.65rem; font-size: 0.95rem; background: var(--bg-surface-alt); color: var(--text-main); outline: none; font-family: var(--font-body); transition: border-color 0.2s, box-shadow 0.2s;" />
              <span id="modal-name-error" style="display: none; color: #EF4444; font-size: 0.75rem; font-weight: 600; margin-top: 0.35rem;"></span>
            </div>

            <div style="display: flex; flex-direction: column; width: 100%;">
              <label style="display: block; font-size: 0.8rem; font-weight: 700; color: var(--text-main); margin-bottom: 0.35rem;">Tipo / Frequência</label>
              <div class="home-scope-switcher" id="modal-type-switcher" style="width: 100%; display: flex; box-sizing: border-box;">
                <button type="button" class="home-scope-pill active" data-type="semanal" style="flex: 1; text-align: center; height: 34px;">Semanal</button>
                <button type="button" class="home-scope-pill" data-type="mensal" style="flex: 1; text-align: center; height: 34px;">Mensal</button>
                <button type="button" class="home-scope-pill" data-type="avulsa" style="flex: 1; text-align: center; height: 34px;">Avulsa</button>
              </div>
            </div>

            <div style="display: flex; flex-direction: column; width: 100%;">
              <label style="display: block; font-size: 0.8rem; font-weight: 700; color: var(--text-main); margin-bottom: 0.35rem;">Mês de Referência</label>
              <input type="month" id="input-list-month" value="${currentMonthStr}" style="width: 100%; box-sizing: border-box; padding: 0.75rem 0.85rem; border: 1px solid var(--border-color); border-radius: 0.65rem; font-size: 0.95rem; background: var(--bg-surface-alt); color: var(--text-main); outline: none; font-family: var(--font-body);" />
            </div>

            <div style="display: flex; flex-direction: column; width: 100%;">
              <label style="display: block; font-size: 0.8rem; font-weight: 700; color: var(--text-main); margin-bottom: 0.35rem;">Compartilhamento</label>
              <div class="home-scope-switcher" id="modal-scope-switcher" style="width: 100%; display: flex; box-sizing: border-box;">
                <button type="button" class="home-scope-pill ${currentScope === 'personal' ? 'active' : ''}" data-scope="personal" style="flex: 1; text-align: center; height: 34px;">Pessoal</button>
                <button type="button" class="home-scope-pill ${currentScope === 'family' ? 'active' : ''}" data-scope="family" style="flex: 1; text-align: center; height: 34px;">Família</button>
              </div>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 0.75rem; margin-top: 0.5rem;">
              <button type="button" id="btn-cancel-create-modal" class="btn btn-secondary" style="padding: 0.65rem 1.15rem; border-radius: var(--radius-pill); font-weight: 700; cursor: pointer;">Cancelar</button>
              <button type="button" id="btn-save-lista" class="btn btn-primary" style="padding: 0.65rem 1.35rem; border-radius: var(--radius-pill); font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; gap: 0.4rem; min-width: 120px;">Salvar Lista</button>
            </div>
          </form>
        </div>
      </div>
    `;

    const closeBtn = modalContainer.querySelector('#btn-close-create-modal');
    const cancelBtn = modalContainer.querySelector('#btn-cancel-create-modal');
    const backdrop = modalContainer.querySelector('#modal-create-backdrop');
    const close = () => { modalContainer.innerHTML = ''; };
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (cancelBtn) cancelBtn.addEventListener('click', close);
    if (backdrop) {
      backdrop.addEventListener('click', (e) => {
        if (e.target === backdrop) close();
      });
    }

    // Segmented switchers logic
    let selectedType = 'semanal';
    let selectedScope = currentScope;

    modalContainer.querySelectorAll('#modal-type-switcher .home-scope-pill').forEach(btn => {
      btn.addEventListener('click', () => {
        modalContainer.querySelectorAll('#modal-type-switcher .home-scope-pill').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        selectedType = btn.getAttribute('data-type') || 'semanal';
      });
    });

    modalContainer.querySelectorAll('#modal-scope-switcher .home-scope-pill').forEach(btn => {
      btn.addEventListener('click', () => {
        modalContainer.querySelectorAll('#modal-scope-switcher .home-scope-pill').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        selectedScope = btn.getAttribute('data-scope') || 'personal';
      });
    });

    const form = modalContainer.querySelector('#form-create-lista');
    const btnSave = modalContainer.querySelector('#btn-save-lista');
    const inputName = modalContainer.querySelector('#input-list-name');
    const errorMsg = modalContainer.querySelector('#modal-name-error');

    if (inputName) {
      inputName.addEventListener('input', () => {
        inputName.style.borderColor = 'var(--border-color)';
        inputName.style.boxShadow = 'none';
        if (errorMsg) errorMsg.style.display = 'none';
      });
    }

    let isSubmitting = false;

    const handleSave = async (e) => {
      if (e) e.preventDefault();
      if (isSubmitting) return;

      const name = inputName?.value ? inputName.value.trim() : '';
      if (!name) {
        if (inputName) {
          inputName.style.borderColor = '#EF4444';
          inputName.style.boxShadow = '0 0 0 3px rgba(239, 68, 68, 0.2)';
          inputName.focus();
        }
        if (errorMsg) {
          errorMsg.textContent = 'Por favor, digite o nome da lista.';
          errorMsg.style.display = 'block';
        }
        return;
      }

      isSubmitting = true;
      if (btnSave) {
        btnSave.disabled = true;
        btnSave.style.opacity = '0.7';
        btnSave.innerHTML = `<span class="material-symbols-outlined spinning" style="font-size: 16px;">progress_activity</span> Salvando...`;
      }

      const type = selectedType;
      const monthRef = modalContainer.querySelector('#input-list-month')?.value || currentMonthStr;
      const scope = selectedScope;

      const payload = {
        name,
        type,
        monthRef,
        scope,
        status: 'aberta',
        totalEstimated: 0,
        totalActual: 0,
      };

      let newId = null;

      try {
        if (authManager.isAuthenticated()) {
          try {
            const res = await authManager.fetchWithAuth('/api/listas', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            });
            if (res.ok) {
              const created = await res.json();
              await saveListaLocal(created);
              newId = created.id;
            } else {
              const localCreated = await saveListaLocal(payload);
              newId = localCreated.id;
            }
          } catch (err) {
            console.warn('API list create fallback to local:', err);
            const localCreated = await saveListaLocal(payload);
            newId = localCreated.id;
          }
        } else {
          const localCreated = await saveListaLocal(payload);
          newId = localCreated.id;
        }

        currentScope = scope;
        setSavedScope(scope);

        close();

        if (newId) {
          window.location.hash = `#lista/${newId}`;
        } else {
          await fetchListas();
          render();
        }
      } catch (err) {
        console.error('Erro ao salvar lista:', err);
        isSubmitting = false;
        if (btnSave) {
          btnSave.disabled = false;
          btnSave.style.opacity = '1';
          btnSave.innerHTML = 'Salvar Lista';
        }
        if (errorMsg) {
          errorMsg.textContent = 'Erro ao salvar a lista. Tente novamente.';
          errorMsg.style.display = 'block';
        }
      }
    };

    if (btnSave) {
      btnSave.addEventListener('click', handleSave);
    }
    if (form) {
      form.addEventListener('submit', handleSave);
    }
  }

  render();
}
